import { forwardRef } from 'react';
import { FieldError } from '../ui/FormField';

/**
 * Shared building blocks for the digit-mask pickers (DatePicker, TimePicker, TimeRangePicker).
 */

export function focusSegment(ref) {
  ref?.current?.focus();
  ref?.current?.select();
}

export function parseTwoDigitSegment(raw, max) {
  let value = raw.replace(/\D/g, '').slice(0, 2);
  if (value.length === 1 && Number(value) > Math.floor(max / 10)) {
    return { value, complete: true };
  }
  if (value.length === 2 && Number(value) > max) value = String(max);
  return { value, complete: value.length === 2 };
}

export function padSegment(str, min, max) {
  return String(Math.max(min, Math.min(max, Number(str)))).padStart(2, '0');
}

export function splitTime(value) {
  if (!value) return ['', ''];
  const [h, m] = value.split(':');
  return [h ?? '', m ?? ''];
}

export function formatTime(hourStr, minuteStr) {
  return `${padSegment(hourStr, 0, 23)}:${padSegment(minuteStr, 0, 59)}`;
}

export const Segment = forwardRef(function Segment({ isFocused, maxLength = 2, width = 'w-5', ...inputProps }, ref) {
  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={maxLength}
      placeholder={'_'.repeat(maxLength)}
      className={`${width} rounded text-sm text-left focus:outline-none placeholder:text-slate-400 disabled:text-slate-400 disabled:cursor-not-allowed`}
      style={{ backgroundColor: isFocused ? '#f1f5f9' : 'transparent' }}
      {...inputProps}
    />
  );
});

export function SegmentSeparator({ children, className = '' }) {
  return <span className={`text-sm text-slate-400 ${className}`}>{children}</span>;
}

export function SegmentHint({ children }) {
  return <span className="text-xs text-slate-300 ml-auto select-none whitespace-nowrap">{children}</span>;
}


export const SegmentBox = forwardRef(function SegmentBox({ onBlur, onClick, error, disabled = false, children }, ref) {
  return (
    <div className="flex flex-col">
      <div
        ref={ref}
        onBlur={onBlur}
        onClick={onClick}
        className={`input input-bordered h-9 px-3 flex items-center gap-1 focus-within:outline-none
          ${error ? 'input-error' : ''}
          ${disabled ? 'bg-slate-100 cursor-not-allowed' : 'cursor-text'}`}
      >
        {children}
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
});